import React, { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import AuditTargetSelect from './AuditTargetSelect';
import { sandboxService } from '../../services/sandboxService';
import { TransferTargetAudit } from '../../types';
import { extractApiError } from '../../utils/errorUtils';

export interface SandboxImportTarget {
  id: number;
  titel: string;
}

export interface SandboxImportResult {
  schwachstelle_id: number;
  audit_id: number;
  audit_name: string;
  imported: { schwachstelle_id: number; audit_id: number; audit_name: string }[];
}

interface Props {
  open: boolean;
  findings: SandboxImportTarget[];
  onClose: () => void;
  onImported: (result: SandboxImportResult) => void;
}

/** Ein oder mehrere Sandbox-Findings in ein Audit importieren (nur Hauptaccount). */
const SandboxImportDialog: React.FC<Props> = ({ open, findings, onClose, onImported }) => {
  const [audit, setAudit] = useState<TransferTargetAudit | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const count = findings.length;
  const single = findings[0];

  const handleConfirm = async () => {
    if (!audit || count === 0) return;
    setSaving(true);
    setError(null);
    try {
      if (count === 1) {
        const res = await sandboxService.import(single.id, audit.id);
        onImported({ ...res, imported: [res] });
      } else {
        const res = await sandboxService.importMany(findings.map((f) => f.id), audit.id);
        onImported({
          schwachstelle_id: res.imported[0]?.schwachstelle_id ?? findings[0].id,
          audit_id: res.audit_id,
          audit_name: res.audit_name,
          imported: res.imported,
        });
      }
    } catch (e) {
      setError(extractApiError(e, 'Import fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {count > 1 ? `${count} Findings in Audit importieren` : 'In Audit importieren'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: count > 1 ? 1 : 2 }}>
          {count <= 1
            ? `${single?.titel ? `„${single.titel}“` : 'Das Finding'} wird mit allen Screenshots und Anhängen in das gewählte Audit übernommen und verschwindet aus der isolierten Umgebung.`
            : `${count} Findings werden mit allen Screenshots und Anhängen in das gewählte Audit übernommen und verschwinden aus der isolierten Umgebung.`}
        </Typography>
        {count > 1 && (
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2.5 }} data-testid="sandbox-import-list">
            {findings.map((f) => (
              <Typography key={f.id} component="li" variant="body2">
                {f.titel || `Finding #${f.id}`}
              </Typography>
            ))}
          </Box>
        )}
        <AuditTargetSelect active={open} onChange={(a) => setAudit(a)} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: 'text.secondary' }}>Abbrechen</Button>
        <Button onClick={() => void handleConfirm()} variant="contained" disabled={!audit || saving || count === 0} data-testid="sandbox-import-confirm">
          {saving ? 'Importieren…' : count > 1 ? `${count} importieren` : 'Importieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SandboxImportDialog;
